import { useState } from 'react'
import { Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { PageBar } from '@/app/layout/page-bar'
import { ApplicationList } from '@/features/application/components/application-list'
import { CreateApplicationDialog } from '@/features/application/components/create-application-dialog'
import type { ListingTab } from '@/features/application/listing'
import { WorkspaceActions } from '@/features/workspace/components/workspace-actions'
import { useTenantWorkspace } from '@/features/workspace/use-tenant-workspace'
import { Button } from '@/shared/ui/button'
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/shared/ui/empty'
import { Skeleton } from '@/shared/ui/skeleton'

/** The page's address: the tab, the search and each filter's comma-separated values. */
export interface DashboardSearch {
  tab?: Exclude<ListingTab, 'mine'> | undefined
  search?: string | undefined
  type?: string | undefined
  owner?: string | undefined
  access?: string | undefined
  modified?: string | undefined
}

/**
 * Where a signed-in user lands, and the tenant's workspace itself.
 *
 * There is nothing above it to list. A tenant has one workspace, so the landing page
 * is that workspace rather than a way of choosing between several -- upstream's
 * onboarding flow and this fork's workspace grid both answered a question nobody is
 * asked any more.
 */
export function DashboardPage() {
  const { t } = useTranslation()
  const { workspace, isPending } = useTenantWorkspace()

  if (isPending) {
    return (
      <Page>
        <div className="space-y-4 p-6">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-32 w-full" />
        </div>
      </Page>
    )
  }

  // Only reachable for an account the identity provider has not placed in a tenant,
  // which is a state to explain rather than a page to render empty.
  if (workspace === undefined) {
    return (
      <Page>
        <Empty className="p-6">
          <EmptyHeader>
            <EmptyTitle>{t('workspace.notFoundTitle')}</EmptyTitle>
            <EmptyDescription>{t('workspace.notFoundDescription')}</EmptyDescription>
          </EmptyHeader>
        </Empty>
      </Page>
    )
  }

  return (
    <Page>
      <div className="p-6">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold">{workspace.name}</h1>
            <p className="text-sm text-muted-foreground">
              {t('workspace.members', { count: workspace.users.length })}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <CreateButtons workspaceId={workspace.id} />
            <WorkspaceActions workspace={workspace} />
          </div>
        </header>

        <ApplicationList workspace={workspace} />
      </div>
    </Page>
  )
}

/**
 * The page's own frame: its bar, and the one part of it that scrolls.
 *
 * The shell gives a page room rather than a scroll container, so a page that is
 * longer than the window says so here. The bar stays where it is while the rest
 * of it moves.
 */
function Page({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full min-h-0 flex-col">
      <PageBar />
      <div className="min-h-0 flex-1 overflow-auto">{children}</div>
    </div>
  )
}

function CreateButtons({ workspaceId }: { workspaceId: number }) {
  const { t } = useTranslation()
  const [creating, setCreating] = useState<'database' | 'dashboard' | null>(null)

  return (
    <div className="flex gap-2">
      <Button onClick={() => setCreating('database')} className="on-surface">
        <Plus />
        {t('application.create.database')}
      </Button>
      <Button
        variant="outline"
        onClick={() => setCreating('dashboard')}
        className="on-surface"
      >
        <Plus />
        {t('application.create.dashboard')}
      </Button>

      {creating !== null && (
        // Mounted only while open, and keyed by type, so the form inside starts
        // empty each time rather than holding the last name that was typed.
        <CreateApplicationDialog
          key={creating}
          workspaceId={workspaceId}
          type={creating}
          open
          onOpenChange={(open) => {
            if (!open) setCreating(null)
          }}
        />
      )}
    </div>
  )
}
